import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const languages = [
  { code: 'es', name: 'Español', flag: '🇨🇴' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

export const LanguageSelector = () => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === language);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (code) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center gap-2 bg-[#121215] hover:bg-[#18181c] text-neutral-300 hover:text-gold-400 border border-neutral-800 hover:border-gold-400/40 px-3 py-2 rounded text-xs uppercase tracking-wider transition-all"
        data-testid="language-selector-button"
      >
        <Globe size={15} className="text-gold-400" />
        <span className="hidden sm:inline text-xs">{currentLanguage?.flag}</span>
        <span className="hidden md:inline text-xs font-medium">{currentLanguage?.name}</span>
        <ChevronDown size={14} className={`text-neutral-500 transition-transform ${isOpen ? 'rotate-180 text-gold-400' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-0 mt-2 bg-[#131316] border border-neutral-800 rounded-lg min-w-[180px] z-50 shadow-2xl overflow-hidden py-1"
            data-testid="language-dropdown"
          >
            {languages.map((lang) => (
              <motion.button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                whileHover={{ backgroundColor: 'rgba(223, 192, 147, 0.08)' }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs uppercase tracking-wider transition-colors ${
                  language === lang.code ? 'bg-gold-400/10 text-gold-400 font-semibold' : 'text-neutral-400'
                }`}
                data-testid={`language-option-${lang.code}`}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="text-xs">{lang.name}</span>
                {language === lang.code && (
                  <span className="ml-auto text-gold-400">✦</span>
                )}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
